import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode,
} from 'react';
import { Modal } from 'antd';
import { appMessage as message } from '../utils/appMessage';
import type { TableRowSelection } from 'antd/es/table/interface';
import { Card, CardType } from '../types/card';
import { JOB_TYPE_LABEL, JobStatus, JobType } from '../types/job';
import { syncStock, applyStock } from '../api/api';
import { STORAGE_KEY, useStorage } from './StorageContext';
import { useCardPage } from './CardPageContext';
import { useListSelection } from '../hooks/UseListSelection';
import { useCardFilters, FilterOption } from '../hooks/UseCardView';
import { useJobRunner } from '../hooks/UseJobRunner';

// ─── 카트 항목 ────────────────────────────────────────────────────────────────
// value = 현재 재고에 대한 증감량 (+3, -2, 0=현상유지)
export interface CartItem {
  value: number;
  /**
   * 이 증감의 요청 식별자(UUID, 감사 card-controltower#277) — 반영을 보낼 때 정하고, 같은 증감으로 다시 보낼 때 그대로 쓴다.
   * 증감량을 바꾸면 새 요청이라 지운다. 성공한 항목은 카트에서 빠지며 함께 사라진다.
   */
  requestId?: string;
}

/** 요청 식별자 — crypto.randomUUID 가 없으면(옛 브라우저 · http) 시각 + 난수로 만든다. */
export function newRequestId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch { /* 아래 폴백 */ }
  return `cart-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ─── 카트 영속화 (localStorage) ────────────────────────────────────────────────
// 카트는 휘발성 메모리가 아니라 localStorage 에 보관한다. 직렬화 형식은 [id, value][].
//
// 키는 매장별로 분리한다 — 워크플로우 키(UseDailyWorkflowStorage.dailyKey)와 같은 우선순위
// (superStore → store → 'none', http.ts 의 X-Store-Code 판단과 동일)로 매장 코드를 붙인다.
// 예전엔 전역 키 하나만 써서 SUPER 가 매장을 바꿔도 이전 매장의 카트가 그대로 보였다
// (2026-10-19 리뷰 #4).
function cartKey(storeCode: string): string {
  return `${STORAGE_KEY.stockCart}:${storeCode}`;
}

function loadCart(storeCode: string): Map<number, CartItem> {
  try {
    const raw = localStorage.getItem(cartKey(storeCode));
    if (!raw) return new Map();
    // [id, value] (예전) · [id, value, requestId] (감사 #277) 둘 다 읽는다.
    const entries = JSON.parse(raw) as [number, number, string?][];
    return new Map(entries.map(([id, value, requestId]) => [id, requestId ? { value, requestId } : { value }]));
  } catch {
    return new Map();
  }
}

/**
 * 매장별 키로 바꾸기 전의 전역 키(stock:cart)에 남은 카트를 버리고, 버린 줄 수를 돌려준다(없으면 0).
 * 예전에는 처음 이 키를 읽는 매장으로 옮겼는데, 그 카트가 어느 매장 것인지 알 수 없어 부산에서 남긴 증감량이 광주의
 * 같은 카드 ID 로 복원 · 전송될 수 있었다(2026-10-22 코덱 전체 점검, 감사 card-controltower#105) — 복원하지 않는다.
 */
function dropLegacyCart(): number {
  try {
    const legacy = localStorage.getItem(STORAGE_KEY.stockCart);
    if (legacy === null) return 0;
    localStorage.removeItem(STORAGE_KEY.stockCart);
    const entries = JSON.parse(legacy) as unknown;
    return Array.isArray(entries) ? entries.length : 0;
  } catch {
    return 0;
  }
}

function saveCart(storeCode: string, cart: Map<number, CartItem>) {
  const entries = Array.from(cart.entries()).map(([id, item]) => (item.requestId ? [id, item.value, item.requestId] : [id, item.value]));
  localStorage.setItem(cartKey(storeCode), JSON.stringify(entries));
}

interface StockPageContextValue {
  cards:   Card[];
  loading: boolean;
  reload:  () => Promise<boolean | void>;
  error:   Error | null;
  retry:   () => void;
  /** 성공한 카드 조회가 한 번이라도 있었는지 — 첫 조회 실패와 진짜 0건을 구분한다. */
  hasLoadedOnce: boolean;
  /** 재조회 실패 토스트 억제 토글 — 인라인 배너를 그리는 화면이 마운트 동안만 켠다. */
  setInlineError: (on: boolean) => void;

  // 필터 (가격 변동 기준 quickFilter 는 쓰지 않는다 — 재고는 가격과 무관). useCardFilters를
  // 가격 탭(useCardView)과는 독립된 인스턴스로 호출해 상태를 공유하지 않는다.
  search:     string;
  setSearch:  (v: string) => void;
  typeFilter: CardType | 'ALL';
  setTypeFilter: (v: CardType | 'ALL') => void;
  expansionFilter: string;
  setExpansionFilter: (v: string) => void;
  expansionOptions: FilterOption[];
  rarityFilter: string[];
  setRarityFilter: (v: string[]) => void;
  rarityOptions: FilterOption[];
  saleStatusFilter: string;
  setSaleStatusFilter: (v: string) => void;
  saleStatusOptions: FilterOption[];
  saleStatusCounts: Record<string, number>;
  filtered:   Card[];

  // 선택
  selectedIds:         number[];
  rowSelection:        TableRowSelection<Card>;
  allFilteredSelected: boolean;
  selectAllFiltered:   () => void;
  clearSelection:      () => void;

  // 카트
  cart:               Map<number, CartItem>;
  cartCards:          Card[];
  addToCart:          (ids: number[]) => void;
  removeFromCart:     (id: number) => void;
  clearCart:          () => void;
  setCartItem:        (id: number, item: CartItem) => void;
  resolveTargetStock: (card: Card) => number;

  // 잡
  activeJobId:       string | null;
  syncing:           boolean;
  applying:          boolean;
  // 반환값: 잡이 정상적으로 시작됐는지 여부. DailyWorkflowBar가 "동기화 먼저, 완료되면
  // 가격 조회 시작"을 체이닝할 때, 동기화 시작 자체가 실패하면(네트워크 오류 등) 기다리지
  // 않고 바로 가격 조회로 넘어가도록 판단하는 데 쓴다.
  startSync:         (ids: number[]) => Promise<boolean>;
  /** 재고 반영 잡을 실제로 시작했으면 true(빈 카트 · 변경 없음 · 진행 중 · 시작 실패는 false — 감사 #535). */
  applyCart:         () => Promise<boolean>;
  handleJobComplete: (job: JobStatus) => void;
}

const StockPageContext = createContext<StockPageContextValue | null>(null);

export function StockPageProvider({ children }: { children: ReactNode }) {
  // ── 상품 목록 ───────────────────────────────────────────────────────────────
  // CardPageProvider(부모, MainApp에서 항상 이 Provider를 감싼다)가 이미 fetchCards로
  // 불러온 목록을 그대로 쓴다 — 예전엔 여기서 useEntityList(fetchCards, ...)를 따로 호출해서
  // 앱 로드마다 /api/cards 전체(수천 건)가 두 번 나갔다.
  const { cards: cardsData } = useCardPage();
  const { cards, loading, reload, error, retry, mergeResults, hasLoadedOnce, setInlineError } = cardsData;

  // ── 필터 ──────────────────────────────────────────────────────────────────
  // 가격 탭(useCardView)과 같은 필터링 파이프라인(useCardFilters)을 쓰되, 독립된 인스턴스라
  // 검색어·종류·확장팩·레어도·판매상태 선택이 탭끼리 서로 영향을 주지 않는다.
  const {
    search, setSearch, typeFilter, setTypeFilter,
    expansionFilter, setExpansionFilter, expansionOptions,
    rarityFilter, setRarityFilter, rarityOptions,
    saleStatusFilter, setSaleStatusFilter, saleStatusOptions, saleStatusCounts,
    filtered,
  } = useCardFilters(cards);

  // ── 선택 ──────────────────────────────────────────────────────────────────
  const { selectedIds, rowSelection, allFilteredSelected, selectAllFiltered, clearSelection } =
    useListSelection(filtered);

  // ── 카트 ──────────────────────────────────────────────────────────────────
  // 초기값은 현재 매장의 localStorage 에서 복원하고, 변경될 때마다 다시 저장한다(비휘발성).
  // 매장 판단은 http.ts 의 X-Store-Code 와 같은 우선순위(superStore → store → 'none').
  const { store, superStore } = useStorage();
  const activeStore = superStore || store || 'none';

  const [cart, setCart] = useState<Map<number, CartItem>>(() => loadCart(activeStore));
  // 현재 cart state 가 어느 매장의 키에서 읽힌 것인지.
  const cartStoreRef = useRef(activeStore);

  useEffect(() => {
    if (cartStoreRef.current !== activeStore) {
      // 매장이 바뀌면 그 매장의 카트로 갈아끼운다. 이번 커밋에서는 저장하지 않고 돌아간다 —
      // 아직 이전 매장 카트가 남아 있는 state 를 새 매장 키에 쓰면 그 매장 카트를 덮어쓴다.
      cartStoreRef.current = activeStore;
      setCart(loadCart(activeStore));
      return;
    }
    saveCart(activeStore, cart);
  }, [activeStore, cart]);

  // 옛 전역 카트는 매장이 정해진 뒤(SUPER 전체 개요 'none' 이 아닐 때) 한 번 버리고 알린다 — 감사 #105.
  useEffect(() => {
    if (activeStore === 'none') return;
    const dropped = dropLegacyCart();
    if (dropped > 0) {
      message.warning(`예전 버전에서 담아 둔 재고 카트 ${dropped.toLocaleString()}건은 어느 매장 것인지 알 수 없어 비웠습니다 — 필요하면 다시 담아 주세요.`);
    }
  }, [activeStore]);

  const addToCart = useCallback((ids: number[]) => {
    setCart((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => { if (!next.has(id)) next.set(id, { value: 0 }); });
      return next;
    });
  }, []);

  const removeFromCart = useCallback((id: number) => {
    setCart((prev) => { const next = new Map(prev); next.delete(id); return next; });
  }, []);

  const clearCart = useCallback(() => setCart(new Map()), []);

  const setCartItem = useCallback((id: number, item: CartItem) => {
    setCart((prev) => {
      const next = new Map(prev);
      const before = prev.get(id);
      // 증감량이 그대로면 요청 식별자를 이어 쓰고(같은 요청), 바뀌면 새 요청이라 버린다(감사 #277).
      const requestId = item.requestId
        ?? (before && Math.floor(before.value) === Math.floor(item.value) ? before.requestId : undefined);
      next.set(id, requestId ? { value: item.value, requestId } : { value: item.value });
      return next;
    });
  }, []);

  const cardById = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards]);
  const cartCards = useMemo(
    () => Array.from(cart.keys())
      .map((id) => cardById.get(id))
      .filter((c): c is Card => !!c),
    [cart, cardById],
  );

  // 현재 재고 + 증감량. 음수는 0 으로 클램프.
  const resolveTargetStock = useCallback((card: Card): number => {
    const item = cart.get(card.id);
    if (!item) return card.stockQuantity ?? 0;
    return Math.max(0, (card.stockQuantity ?? 0) + Math.floor(item.value));
  }, [cart]);

  // ── 잡 (재고 동기화 / 재고 반영) ──────────────────────────────────────────
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [syncing, setSyncing]         = useState(false);
  const [applying, setApplying]       = useState(false);

  // 반영 시작 요청이 응답을 기다리는 동안의 연타를 로딩 표시 관점에서도 막기 위한 표식
  // (2026-10-19 리뷰 #2) — runJob 의 in-flight 가드로 막힌 호출은 false 를 받는데, 그 false 로
  // setApplying(false) 를 하면 첫 요청의 로딩 표시가 먼저 꺼진다. 막힌 호출은 시작 전에 돌려보낸다.
  const applyInFlightRef = useRef(false);

  const runJob = useJobRunner(setActiveJobId);

  const startSync = useCallback((ids: number[]) =>
    runJob(() => syncStock(ids), '동기화 시작 중 오류가 발생했습니다.', () => setSyncing(true)),
    [runJob]);

  // 반영 요청 시점에 작업자가 화면에서 본 값(baseline)과 증감량을 기억해 뒀다가, 잡이
  // 끝나면 실제 반영값과 비교한다 — 그 사이 주문이 들어와 최종 수량이 요청과 다르면
  // 작업자에게 왜 다른지 알린다. 잡 완료 시 cards가 mergeResults로 갱신되므로 baseline은
  // 여기서 미리 붙잡아 둬야 한다.
  const pendingApplyRef = useRef<Map<number, { name: string; baseline: number; delta: number }>>(new Map());

  // 카트 확정: 증감량(delta)을 그대로 Naver 반영 잡으로 넘긴다. 절대값으로 바꾸지 않는
  // 이유는 api.ts applyStock 주석 참고 — 백엔드가 반영 직전 Naver 실측 재고에 이 값을
  // 더한다. 로컬 cards 반영은 잡 완료(handleJobComplete)에서 mergeResults로 일괄 처리한다.
  const applyCart = useCallback(async () => {
    if (cartCards.length === 0) { message.warning('카트가 비어 있습니다.'); return false; }
    // 증감량이 0인(±0) 상품은 반영 대상에서 제외 — 불필요한 GET+PUT 을 줄인다.
    const targets = cartCards
      .map((c) => {
        const item = cart.get(c.id);
        const delta = Math.floor(item?.value ?? 0);
        // 요청 식별자 — 이미 보낸 적 있는 같은 증감이면 그 값을 그대로(재시도), 처음이면 새로 만든다(감사 #277).
        // 보내는 값은 정수(floor) — 저장값이 2.5 여도 보낸 2 와 같으면 같은 요청으로 본다(코덱 셀프 리뷰 높음).
        const requestId = item?.requestId && Math.floor(item.value) === delta ? item.requestId : newRequestId();
        return { id: c.id, name: c.productName, baseline: c.stockQuantity ?? 0, delta, requestId };
      })
      .filter((it) => it.delta !== 0);
    if (targets.length === 0) { message.info('변경된 재고가 없습니다.'); clearCart(); return false; }

    if (applyInFlightRef.current) return false;
    applyInFlightRef.current = true;

    pendingApplyRef.current = new Map(
      targets.map((it) => [it.id, { name: it.name, baseline: it.baseline, delta: it.delta }]),
    );
    // 보낼 요청 식별자를 카트에 먼저 적어 둔다(새로고침 · 다시 열어도 재시도가 같은 값을 쓰게 — localStorage 에 저장된다).
    setCart((prev) => {
      const next = new Map(prev);
      targets.forEach((it) => {
        const cur = next.get(it.id);
        if (cur && cur.requestId !== it.requestId) next.set(it.id, { value: cur.value, requestId: it.requestId });
      });
      return next;
    });

    setApplying(true);
    try {
      const started = await runJob(
        () => applyStock(targets.map((it) => ({ id: it.id, delta: it.delta, requestId: it.requestId }))),
        '재고 반영 중 오류가 발생했습니다.',
      );
      // 시작 자체가 실패(에러)한 경우에만 로딩을 해제한다 — 성공하면 잡 완료(handleJobComplete)가
      // 해제한다. in-flight 가드로 막힌 호출은 위에서 이미 돌려보냈으므로 여기까지 오지 않는다.
      if (!started) setApplying(false);
      return started;
    } finally {
      applyInFlightRef.current = false;
    }
  }, [cartCards, cart, clearCart, runJob]);

  // job.type별 완료 처리 — UseCardData.ts의 useCardJobs와 같은 Strategy 패턴(2026-08-11).
  const handleJobComplete = useCallback((job: JobStatus) => {
    setActiveJobId(null);
    setSyncing(false);
    setApplying(false);
    // 실패(또는 서버에서 사라진 잡)는 잠금 · 로딩만 풀고 종류별 완료 처리(성공 토스트 · 카트 비우기)는 하지 않는다 —
    // JobProgressBar 가 FAILED 에도 onComplete 를 부르게 된 뒤로(감사 card-controltower#84) 여기로 온다.
    if (job.status === 'FAILED') {
      pendingApplyRef.current = new Map();
      message.error(`${JOB_TYPE_LABEL[job.type] ?? '작업'} 실패 — ${job.error ?? '알 수 없는 오류'}`);
      return;
    }
    if (job.results) mergeResults(job.results);

    const handlers: Partial<Record<JobType, () => void>> = {
      SYNC_STOCK: () => {
        // deletedCount(스토어에서 완전히 삭제된 상품 정리)는 failCount 안에 포함돼 있지만
        // "실패"로만 뜨면 원인이 안 보인다 — 몇 건이 진짜 삭제였는지 따로 알려준다
        // (2026-08-19, "가져오기"쪽과 같은 이유).
        const parts = [`성공 ${job.successCount}건`];
        if (job.failCount > 0) parts.push(`실패 ${job.failCount}건`);
        if (job.deletedCount > 0) parts.push(`스토어에서 삭제된 상품 ${job.deletedCount}건 정리`);
        message.success(`동기화 완료: ${parts.join(' / ')}`);
      },
      APPLY_STOCK: () => {
        // 실패에는 "실측 재고가 기준과 크게 어긋나 반영 보류"(drift 가드)도 포함된다 —
        // 사유는 상품별로 반영 로그에 남으므로 그쪽을 보게 안내한다.
        const base = `재고 반영 완료: 성공 ${job.successCount}건`;
        if (job.failCount > 0) {
          message.warning(`${base} / 실패 ${job.failCount}건 — 사유는 반영 로그에서 확인하세요`);
        } else {
          message.success(base);
        }

        // 반영 시점 스토어 실제 재고가 화면 표시와 달라, 최종 수량이 작업자 요청과
        // 다른 건들을 모아 별도로 알린다(그 사이 들어온 주문 때문 — 작업자가 헷갈리지
        // 않도록). 요청값은 카트 표시와 동일하게 0으로 클램프해서 비교한다.
        const expected = pendingApplyRef.current;
        pendingApplyRef.current = new Map();
        const corrections = (job.results ?? [])
          .filter((r) => r.naverSynced === true && !r.naverError && expected.has(r.id))
          .map((r) => {
            const e = expected.get(r.id)!;
            const applied = r.stockQuantity ?? 0;
            return {
              id: r.id, name: e.name, delta: e.delta, applied,
              requested: Math.max(0, e.baseline + e.delta),
            };
          })
          .filter((c) => c.applied !== c.requested);

        if (corrections.length > 0) {
          Modal.info({
            title: '반영 시점 스토어 재고가 화면 표시와 달랐습니다',
            width: 540,
            content: (
              <div>
                <p style={{ marginTop: 0 }}>
                  아래 상품은 마지막 재고 동기화(매일 07:30) 이후 온라인 주문이 들어와,
                  요청하신 수량이 아니라 <b>스토어 실제 재고 + 증감량</b>으로 반영됐습니다.
                  같은 내역이 <b>반영 로그</b>에도 남습니다.
                </p>
                <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                  {corrections.map((c) => {
                    const sign = c.delta > 0 ? `+${c.delta}` : `${c.delta}`;
                    const impliedLive = c.applied > 0 ? c.applied - c.delta : null;
                    return (
                      <li key={c.id} style={{ marginBottom: 6 }}>
                        <b>{c.name}</b> — 요청 {c.requested}개
                        {impliedLive !== null
                          ? <>, 스토어 실제 재고가 {impliedLive}개였으므로 증감 {sign} 반영하여 <b>최종 {c.applied}개</b></>
                          : <>였으나 스토어 재고가 부족해 <b>최종 {c.applied}개</b></>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ),
          });
        }

        // 반영된 항목만 카트에서 뺀다 — 실패 · 결과 불명 항목은 요청 식별자와 함께 남겨, '반영' 을 다시 누르면 같은 요청으로
        // 다시 보낸다(감사 card-controltower#277). 결과 목록이 없으면(예전 응답) 예전처럼 비운다.
        const results = job.results;
        if (results && results.length > 0) {
          const done = new Set(results.filter((r) => r.naverSynced === true && !r.naverError).map((r) => r.id));
          const left = Array.from(expected.keys()).filter((id) => !done.has(id));
          setCart((prev) => {
            const next = new Map(prev);
            done.forEach((id) => next.delete(id));
            // 이번에 보내지 않은(증감 0) 항목은 정리한다 — 예전처럼.
            Array.from(next.entries()).forEach(([id, item]) => { if (!expected.has(id) && Math.floor(item.value) === 0) next.delete(id); });
            return next;
          });
          if (left.length > 0) {
            message.warning({
              content: `${left.length.toLocaleString()}건은 반영되지 않았거나 결과를 알 수 없어 카트에 남겼습니다 — 다시 '반영' 하면 같은 요청으로 보냅니다(반영 로그에서 사유 확인).`,
              duration: 10,
            });
          }
        } else if (job.failCount > 0) {
          // 결과 목록 없이 실패만 알려 오면 어느 항목인지 모른다 — 카트를 그대로 두어 요청 번호와 함께 다시 보낼 수 있게(코덱 셀프 리뷰 중간).
          message.warning({
            content: `${job.failCount.toLocaleString()}건이 반영되지 않았습니다 — 카트를 그대로 두었으니 반영 로그에서 사유를 확인하고 다시 '반영' 하세요.`,
            duration: 10,
          });
        } else {
          clearCart();
        }
        clearSelection();
      },
    };

    handlers[job.type]?.();
  }, [mergeResults, clearCart, clearSelection]);

  // 하위 트리(재고 테이블 등)가 무관한 상태 변화만으로 리렌더되지 않도록 value를 메모이즈한다.
  const value = useMemo(() => ({
    cards, loading, reload, error, retry, hasLoadedOnce, setInlineError,
    search, setSearch, typeFilter, setTypeFilter,
    expansionFilter, setExpansionFilter, expansionOptions,
    rarityFilter, setRarityFilter, rarityOptions,
    saleStatusFilter, setSaleStatusFilter, saleStatusOptions, saleStatusCounts,
    filtered,
    selectedIds, rowSelection, allFilteredSelected, selectAllFiltered, clearSelection,
    cart, cartCards, addToCart, removeFromCart, clearCart, setCartItem, resolveTargetStock,
    activeJobId, syncing, applying, startSync, applyCart, handleJobComplete,
  }), [
    cards, loading, reload, error, retry, hasLoadedOnce, setInlineError,
    search, setSearch, typeFilter, setTypeFilter,
    expansionFilter, setExpansionFilter, expansionOptions,
    rarityFilter, setRarityFilter, rarityOptions,
    saleStatusFilter, setSaleStatusFilter, saleStatusOptions, saleStatusCounts,
    filtered,
    selectedIds, rowSelection, allFilteredSelected, selectAllFiltered, clearSelection,
    cart, cartCards, addToCart, removeFromCart, clearCart, setCartItem, resolveTargetStock,
    activeJobId, syncing, applying, startSync, applyCart, handleJobComplete,
  ]);

  return (
    <StockPageContext.Provider value={value}>
      {children}
    </StockPageContext.Provider>
  );
}

export function useStockPage(): StockPageContextValue {
  const ctx = useContext(StockPageContext);
  if (!ctx) throw new Error('useStockPage must be used within StockPageProvider');
  return ctx;
}
